import { type SupplierListItem } from '@choco-admin/types';
import { Card } from '@heroui/react';
import React from 'react';
import { Controller } from 'react-hook-form';

import { AlertCard } from '../../../../../../components/AlertCard';
import { ChocoCheckbox } from '../../../../../../components/FormComponents';
import {
  isIntegrationLocked,
  useIntegrationSettingsAccess,
} from '../../../../../../featureFlags';
import { IntegrationLockNotice, LockedFieldTooltip } from '../../components';
import { useSupplierForm } from '../../SupplierFormProvider';

import { CatalogProductBrowser, DefaultCatalogField } from './components';

interface CatalogTabProps {
  supplier?: SupplierListItem;
}

export const CatalogTab: React.FC<CatalogTabProps> = ({ supplier }) => {
  const { formMethods } = useSupplierForm();

  // Read-only for everyone once the distributor is integrated, and changed on
  // the Settings tab by someone who may change it. The legacy rule.
  const access = useIntegrationSettingsAccess();
  const isIntegrated = Boolean(supplier?.isIntegrated);
  const integrationSettingsLocked = isIntegrationLocked(access, isIntegrated, {
    isPuppy: supplier?.isPuppy,
    isTest: supplier?.isTest,
  });

  if (!supplier) {
    return (
      <AlertCard
        type="info"
        title="Create distributor first"
        message="Please save the distributor to configure catalog settings."
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-8 rounded-xl">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Default Catalog</h3>
            <DefaultCatalogField vendorId={supplier.id} />
          </div>

          <AlertCard
            type="warning"
            title="This distributor has been migrated to LOX"
            message="Catalog content (adding, editing and pricing products) is managed in the Retool app. The product list below is read-only."
          />

          <div>
            <h3 className="text-lg font-semibold mb-4">Prices</h3>
            <div className="mb-4">
              {integrationSettingsLocked && (
                <IntegrationLockNotice
                  title="Locked by this distributor's integration"
                  message="Whether buyers see prices was agreed when this distributor's integration was set up, and those prices come from their own system. It is changed on the Settings tab instead."
                />
              )}
              <div className={isIntegrated ? 'mt-2' : ''}>
                <LockedFieldTooltip
                  isLocked={integrationSettingsLocked}
                  canChange={access.canChange}
                >
                  <Controller
                    name="displayToCustomers"
                    control={formMethods.control}
                    defaultValue={
                      supplier?.priceSetting?.displayToCustomers ?? false
                    }
                    render={({ field: { value, onChange } }) => (
                      <ChocoCheckbox
                        checked={value}
                        isSelected={value}
                        onChange={(e) => onChange(e.target.checked)}
                        isDisabled={integrationSettingsLocked}
                        label="Show prices in catalogs for all customers"
                        description="Customers, who the distributor already has a chat with on Choco, will be able to see prices."
                        color="primary"
                      />
                    )}
                  />
                </LockedFieldTooltip>
              </div>
            </div>

            <div className="mb-4">
              <Controller
                name="displayInMarketplace"
                control={formMethods.control}
                defaultValue={supplier?.priceSetting?.displayInMarketplace}
                render={({ field: { value, onChange } }) => {
                  return (
                    <ChocoCheckbox
                      checked={value}
                      isSelected={value}
                      onChange={(e) => onChange(e.target.checked)}
                      label="Show prices to distributor users"
                      description="All distributor users will be able to see catalog/negotiated prices."
                      color="primary"
                    />
                  );
                }}
              />
            </div>

            <div>
              <Controller
                name="supplierHideMyPrice"
                control={formMethods.control}
                defaultValue={supplier?.hideMyPrice ?? false}
                render={({ field: { value, onChange } }) => (
                  <ChocoCheckbox
                    checked={value}
                    isSelected={value}
                    onChange={(e) => onChange(e.target.checked)}
                    label="Hide My Price"
                    description="Prevents users from being able to view or update My Price via the app"
                    color="primary"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-8 rounded-xl">
        <CatalogProductBrowser supplier={supplier} />
      </Card>
    </div>
  );
};
